// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { useT } from "../i18n/LanguageContext.jsx";

export default function LoginPage() {
  const { loginWithCode, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const redirectTo = location.state?.from?.pathname || "/simulator";

  React.useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, redirectTo, navigate]);

  const login = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setBusy(true);
      setError(null);
      try {
        await loginWithCode(code);
        navigate(redirectTo, { replace: true });
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || t("login.errors.failed");
        setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      } finally {
        setBusy(false);
      }
    },
    onError: (err) => {
      setError(err?.error_description || t("login.errors.cancelled"));
    },
  });

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-24" data-testid="login-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div>
          <div className="data-label mb-4">{t("login.kicker")}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05] mb-5">
            {t("login.titleA")}
            <br />
            <span className="text-slate-400">{t("login.titleB")}</span>
          </h1>
          <p className="text-sm text-slate-600 max-w-md leading-relaxed">
            {t("login.description")}
          </p>
          <ul className="mt-6 text-[12px] font-mono text-slate-500 space-y-1.5">
            <li>01 · {t("login.step1")}</li>
            <li>02 · {t("login.step2")}</li>
            <li>03 · {t("login.step3")}</li>
            <li>
              04 · {t("login.step4")} <code>{redirectTo}</code>
            </li>
          </ul>
        </div>

        <div className="border border-slate-200 p-8 clinical-grid">
          <div className="data-label mb-4">{t("login.provider")}</div>
          <button
            data-testid="btn-google-login"
            disabled={busy}
            onClick={() => login()}
            className="w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
              <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-5.1C29 35.3 26.6 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.7 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6 5.1C40.1 35.5 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
            </svg>
            <span className="text-sm font-medium text-slate-900">
              {busy ? t("login.signingIn") : t("login.cta")}
            </span>
          </button>
          {error && (
            <div className="mt-4 p-3 border border-red-300 bg-red-50 text-red-800 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <p className="mt-6 text-[11px] text-slate-500 font-mono leading-relaxed">
            {t("login.note")}
          </p>
        </div>
      </div>
    </div>
  );
}
